import Cookie from 'js-cookie';
import { memoize, clone } from 'lodash-es';
import { AxiosResponse } from 'axios';

import { UserState } from './symbol';
import { httpClient } from '../http';
import { serializableSessionStorage } from '../storage';

const CACHE_KEY = 'stock://userinfo';
const USER_KEY = process.env.VUE_APP_USER_KEY as string;

export { UserState } from './symbol';

/***
 * 对object进行简单的hash(base64)
 * @param obj
 * @returns {string}
 */
function objectHash(obj: any) {
  const orderedObject: any = {};
  Object.keys(obj).forEach((key) => {
    orderedObject[key] = obj[key];
  });

  return btoa(JSON.stringify(orderedObject)).slice(0, 64);
}

/***
 * 从cookie中获取用户的uid
 * @returns {*}
 */
const getUserId = () => Cookie.get(USER_KEY);

/***
 * 获取当前用户的cache-key
 * @param userId
 * @returns {string}
 */
const getCacheKeyPrefix = (userId: string | undefined) =>
  [CACHE_KEY, userId].join('/');

class UserInfo {
  constructor(private readonly storage: Storage) {}

  /***
   * 获取用户信息
   * @param params 请求后台接口时所需要的参数
   * @returns {*}
   */
  getUserInfo(
    params = {
      detail: 1,
      dealer: 1,
    },
  ) {
    return this.getUserInfoMemoized(params, getUserId());
  }

  /***
   * 从缓存->sessionStorage->接口中获取用户信息
   * 不同的user-info请求参数使用不同的缓存key
   * @param params 请求后台接口时所需要的参数
   * @param id 当前用户的uid
   * @returns {Promise} userInfoPromise
   */
  private getUserInfoMemoized = memoize(
    (params, id) => {
      return new Promise<any>((resolve, reject) => {
        const cacheKey = [getCacheKeyPrefix(id), objectHash(params)].join('/');
        const cachedResult = this.storage.getItem(cacheKey);
        if (cachedResult) {
          return resolve(cachedResult);
        }

        httpClient
          .post('userinfo.fcgi', params, { silent: true } as any)
          .then((response: AxiosResponse) => {
            const data = clone(response.data);
            // 删除业务端无需的字段
            delete data['retcode'];
            delete data['retmsg'];

            // 抽象出开户失败状态
            // 在后端眼里，开户失败属于未开户的状态
            // 然而在前端抽象出未开户的状态可以简化判断逻辑
            if (data.userstate === UserState.NO_ACCOUNT && data.failedstep) {
              data.userstate = UserState.FAILED;
            }

            this.storage.setItem(cacheKey, data);
            resolve(data);
          })
          .catch((error) => {
            reject(error.response || error);
          });
      });
    },
    (params, id) => [id, objectHash(params)].join('/'),
  );

  clean() {
    const cacheKeyPrefix = getCacheKeyPrefix(getUserId());

    Object.keys(this.storage).forEach((key) => {
      if (key.indexOf(cacheKeyPrefix) === 0) {
        this.storage.removeItem(key);
      }
    });
  }
}

export const user = new UserInfo(serializableSessionStorage);
